import type { AerCheckboxProps } from "@aerstudios/aerated-aercheckbox/AerCheckbox";
import { AerFieldset } from "@aerstudios/aerated-aerfieldset";
import type {
	DefaultProps,
	HideableTextShape,
} from "@aerstudios/aerated-types/types";
import {
	elementIsHideableTextShape,
	implementsTheSelectableInterface,
} from "@aerstudios/aerated-utilities/dataStructures";
import { hyphenJoin } from "@aerstudios/aerated-utilities/stringUtils";
import cx from "clsx";
import React, {
	type FunctionComponentElement,
	forwardRef,
	isValidElement,
	useCallback,
	useMemo,
	useState,
} from "react";
import * as styles from "./AerCheckboxGroup.module.scss";

const normalisedStyles = "default" in styles ? styles.default : styles;

// todo actually this should be able to accept an array of checkboxes and some help text - see story
type CheckboxChildren = FunctionComponentElement<AerCheckboxProps>[];

export type AerCheckboxGroupProps = DefaultProps<"fieldset"> & {
	/** The checkboxes to group */
	children: CheckboxChildren;
	/** Which checkboxes are selected */
	value?: string[];
	groupLabel: string | HideableTextShape<string>;
	groupLabelClassName?: string;
	onChange?: (value: string[]) => void;
	required?: boolean;
};

/**
 * AerCheckboxGroup allows automatic control of accessible Checkbox buttons
 */
export const AerCheckboxGroup = forwardRef(
	(
		{
			className,
			groupLabelClassName,
			children,
			onChange,
			groupLabel,
			value = [],
			required = false,
			...rest
		}: AerCheckboxGroupProps,
		ref: React.ForwardedRef<HTMLFieldSetElement>,
	) => {
		const [selected, setSelected] = useState<string[]>([...value]);

		const defaultName = hyphenJoin(
			elementIsHideableTextShape(groupLabel) ? groupLabel.text : groupLabel,
		);

		const getHandleChange = useCallback(
			(
				value: string,
				cb?: ((ev: React.ChangeEvent<HTMLInputElement>) => unknown) | null,
			) =>
				(ev: React.ChangeEvent<HTMLInputElement>) => {
					setSelected((currentState) => {
						const ret = [...currentState];
						const valueIndex = ret.findIndex((el) => el === value);

						if (valueIndex > -1) {
							ret.splice(valueIndex, 1);
						} else {
							ret.push(value);
						}

						onChange?.(ret);
						cb?.(ev);
						return ret;
					});
				},
			[onChange],
		);

		const normalisedChildren = useMemo(() => {
			const findCheckboxesAndApplyProps = (
				node: CheckboxChildren,
			): FunctionComponentElement<AerCheckboxProps>[] => {
				return React.Children.map(
					node,
					(child: FunctionComponentElement<AerCheckboxProps>) => {
						if (!isValidElement(child) || typeof child.props !== "object") {
							return child;
						}

						if (implementsTheSelectableInterface(child.props)) {
							const onChange = getHandleChange(
								child.props.value,
								child.props.onChange,
							);

							const checked = selected.includes(child.props.value);

							const clone = React.cloneElement<AerCheckboxProps>(child, {
								...child.props,
								onChange: onChange,
								checked: checked,
								name: defaultName,
							});

							return clone;
						}

						return child;
					},
				);
			};

			return findCheckboxesAndApplyProps(children);
		}, [selected, children, defaultName, getHandleChange]);

		return (
			<AerFieldset
				{...rest}
				ref={ref}
				label={groupLabel}
				className={cx(normalisedStyles.component, className)}
				labelClassName={groupLabelClassName}
			>
				{normalisedChildren}
			</AerFieldset>
		);
	},
);
